import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { Sparkles, Save, Trash2, Search, Bot } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

const CATEGORY_LABELS = {
  nomina: "Nómina", hipoteca: "Hipoteca", alquiler: "Alquiler", servicios: "Servicios",
  supermercado: "Supermercado", restaurantes: "Restaurantes", transporte: "Transporte",
  gasolina: "Gasolina", salud: "Salud", ocio: "Ocio", viajes: "Viajes", compras: "Compras",
  educacion: "Educación", impuestos: "Impuestos", transferencias: "Transferencias",
  ingresos: "Otros ingresos", cripto: "Cripto", inversiones: "Inversiones", otros: "Otros",
};

export default function Rules() {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [edits, setEdits] = useState({}); // { oldKey: { new_key, category, dirty } }

  const load = useCallback(async () => {
    try {
      const res = await api.get("/rules");
      setRules(res.data);
      setEdits({});
    } catch (e) { logger.error("rules load", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setEdit = (oldKey, patch) => {
    setEdits((s) => ({
      ...s,
      [oldKey]: { ...(s[oldKey] || {}), ...patch, dirty: true },
    }));
  };

  const save = async (rule) => {
    const edit = edits[rule.key] || {};
    const new_key = (edit.new_key ?? rule.key).trim().toLowerCase();
    const category = edit.category ?? rule.category;
    if (!new_key) return toast.error("La clave no puede estar vacía");
    try {
      await api.put("/rules", { old_key: rule.key, new_key, category });
      toast.success("Regla actualizada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const remove = async (key) => {
    try {
      await api.delete(`/rules/${encodeURIComponent(key)}`);
      toast.success("Regla eliminada");
      load();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const filtered = rules.filter((r) =>
    !query || r.key.toLowerCase().includes(query.toLowerCase()) ||
    (CATEGORY_LABELS[r.category] || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-end justify-between gap-4 fade-up">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-2 flex items-center gap-2">
            <Bot className="w-3 h-3" /> {t("rules.kicker")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A110E]">{t("rules.title")}</h1>
          <p className="text-[#5F7368] mt-2">
            {t("rules.subtitle", { defaultValue: "La IA aprende de tus correcciones. Aquí puedes ver, renombrar o borrar cada regla." })}
          </p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-[#8A9992] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="rules-search"
            className="pl-9 w-64"
            placeholder={t("rules.searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-[#5F7368]">Cargando…</div>
        ) : rules.length === 0 ? (
          <div className="p-16 text-center">
            <Sparkles className="w-6 h-6 text-[#1C7C54] mx-auto mb-3" />
            <p className="text-[#8A9992]">Aún no hay reglas aprendidas.</p>
            <p className="text-xs text-[#8A9992] mt-2">Corrige la categoría de un movimiento y aparecerá aquí.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#EBEBEA]">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
              <div className="col-span-5">Palabra clave</div>
              <div className="col-span-4">Categoría</div>
              <div className="col-span-1 text-right">Usos</div>
              <div className="col-span-2"></div>
            </div>
            {filtered.map((r) => {
              const edit = edits[r.key] || {};
              const dirty = !!edit.dirty;
              return (
                <div key={r.key} data-testid={`rule-row-${r.key}`} className="grid grid-cols-12 gap-4 px-6 py-3 items-center hover:bg-[#FAFAF9] transition-colors">
                  <div className="col-span-12 md:col-span-5">
                    <Input
                      data-testid={`rule-key-${r.key}`}
                      value={edit.new_key ?? r.key}
                      onChange={(e) => setEdit(r.key, { new_key: e.target.value })}
                      className="h-9 font-mono text-sm"
                    />
                  </div>
                  <div className="col-span-6 md:col-span-4">
                    <Select
                      value={edit.category ?? r.category}
                      onValueChange={(v) => setEdit(r.key, { category: v })}
                    >
                      <SelectTrigger data-testid={`rule-cat-${r.key}`} className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                          <SelectItem key={k} value={k}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3 md:col-span-1 text-right text-sm tabular-nums text-[#5F7368]">
                    {r.count}
                  </div>
                  <div className="col-span-3 md:col-span-2 flex justify-end gap-1">
                    {dirty && (
                      <button
                        onClick={() => save(r)}
                        data-testid={`rule-save-${r.key}`}
                        className="p-2 rounded-lg bg-[#1C7C54] text-white hover:bg-[#0F291E]"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`rule-delete-${r.key}`} className="p-2 rounded-lg hover:bg-[#EBEBEA]">
                          <Trash2 className="w-4 h-4 text-[#D95D39]" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar regla?</AlertDialogTitle>
                          <AlertDialogDescription>La IA dejará de aplicar esta corrección automática.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(r.key)} className="bg-[#D95D39] hover:bg-[#D95D39]/90">Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
